import React, { useEffect, useState } from 'react';
import { Alert, List, Typography, Card, message, Button, Switch } from 'antd';
import axios from 'axios';
import { DownOutlined } from '@ant-design/icons';
import { UpOutlined } from '@ant-design/icons';

interface ValidationError {
  row: number;
  column: string;
  message: string;
}

interface ValidationResult {
  valid: boolean;
  errors?: ValidationError[];
  warnings?: ValidationError[];
  summary?: {
    validation_timestamp: string;
    error_count: number;
    warning_count: number;
  };
}

interface DataCollBasisValidationProps {
  dataCollName: string;
  client: { id: string; token: string };
  hasBasisUploadTable: boolean;
  refreshKey?: number;
  style?: React.CSSProperties;
  onValidationResult?: (result: ValidationResult | null, loading: boolean) => void;
  onRunValidation?: () => void;
  expertModeActive?: boolean;
}
const HIDE_VALIDATION = true; // TODO: remove

export const DataCollBasisValidation: React.FC<DataCollBasisValidationProps> = ({
  dataCollName,
  client,
  hasBasisUploadTable,
  refreshKey,
  style,
  onValidationResult,
  onRunValidation,
  expertModeActive,
}) => {
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);
  const [mockMode, setMockMode] = useState(false);

  useEffect(() => {
    if (!dataCollName || !hasBasisUploadTable) {
      setValidationResult(null);
      onValidationResult?.(null, false);
      return;
    }
    setLoading(true);
    const yearMatch = dataCollName.match(/\d{4}(?=[^0-9]*$)/);
    const year = yearMatch ? parseInt(yearMatch[0], 10) : undefined;
    if (!year) {
      setValidationResult(null);
      onValidationResult?.(null, false);
      setLoading(false);
      return;
    }
    axios
      .post(
        `${process.env.REACT_APP_API_URL}/b2zero/${client.id}/validate_basis_upload`,
        { table_name: `${dataCollName}_basis_upload`, year: year },
        {
          headers: {
            'Authorization': `Bearer ${client.token}`,
            'Content-Type': 'application/json',
          },
        }
      )
      .then((response) => {
        // TODO: remove
        if (mockMode) {
          message.warning('Mock-Fehler aktiviert.');
          response.data.valid = false;
          response.data.errors = [
            { row: 1, column: 'A', message: 'Falscher Datentyp in Spalte A' },
            { row: 2, column: 'B', message: 'Kein Wert in Spalte B' },
          ];
          response.data.summary = response.data.summary || {};
          response.data.summary.error_count = 2;
          setValidationResult(response.data);
          onValidationResult?.(response.data, false);
        } else {
          setValidationResult(response.data);
          onValidationResult?.(response.data, false);
        }
      })
      .catch((error) => {
        setValidationResult({
          valid: false,
          errors: [{ row: 0, column: '', message: error.message }],
        });
        onValidationResult?.(
          {
            valid: false,
            errors: [{ row: 0, column: '', message: error.message }],
          },
          false
        );
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataCollName, hasBasisUploadTable, refreshKey, client.id, client.token]);

  const isCollapsible = hasBasisUploadTable && validationResult?.valid === false;

  if (!hasBasisUploadTable) return null;

  if (HIDE_VALIDATION) return null;

  return (
    <Card
      title={
        <Typography.Title
          level={3}
          style={{ margin: 0, cursor: isCollapsible ? 'pointer' : 'default' }}
          onClick={isCollapsible ? () => setIsExpanded((prev) => !prev) : undefined}
        >
          Validierung der hochgeladenen Stammdatentabelle
          {expertModeActive && (
            <Switch
              checked={mockMode}
              onChange={setMockMode}
              style={{ marginLeft: 12 }}
              checkedChildren="Mock-Fehler anzeigen"
              unCheckedChildren="Echte Validierung"
            />
          )}
        </Typography.Title>
      }
      loading={loading}
      style={{ marginBottom: 12, ...style }}
      extra={
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {validationResult?.summary?.validation_timestamp && (
            <Typography.Text type="secondary">
              {`Stand: ${new Date(validationResult.summary.validation_timestamp).toLocaleString('de-DE')}`}
            </Typography.Text>
          )}
          <Button size="small" onClick={onRunValidation} loading={loading}>
            Erneut prüfen
          </Button>
          {isCollapsible && (
            <Button
              type="text"
              icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
              onClick={() => setIsExpanded((prev) => !prev)}
            />
          )}
        </div>
      }
    >
      {/* Show validation errors if present */}
      {validationResult && validationResult.errors && validationResult.errors.length > 0 && (
        <Alert
          type="error"
          showIcon
          message={
            <>
              <b>{`${validationResult.summary?.error_count || ''} Fehler`}</b> in der
              Stammdatentabelle{' '}
              {!isExpanded && (
                <>
                  {' '}
                  <u>Details anzeigen</u>
                </>
              )}
            </>
          }
          description={
            isExpanded ? (
              <>
                <List
                  size="small"
                  bordered
                  dataSource={validationResult.errors || []}
                  renderItem={(err) => (
                    <List.Item>
                      Zeile {err.row}, Spalte "{err.column}": {err.message}
                    </List.Item>
                  )}
                  style={{ margin: 0, paddingLeft: 0 }}
                />
                <p>
                  {/* TODO: add when Bearbeitungsbereich is optimized. for now force user to delete and reupload */}
                  {/* Sie können diese in dem Bereich{' '} <i>Bearbeitung der hochgeladenen Stammdatentabelle</i> korrigieren. Alternativ */}
                  Bitte korrigieren Sie die hochgeladene Stammdatentabelle und laden Sie sie erneut
                  hoch.
                </p>
              </>
            ) : null
          }
          style={{ marginBottom: 12, cursor: isExpanded ? 'default' : 'pointer' }}
          onClick={() => setIsExpanded(true)}
        />
      )}
      {/* Show validation warnings if present */}
      {validationResult && validationResult.warnings && validationResult.warnings.length > 0 && (
        <Alert
          type="warning"
          showIcon
          message={
            <>
              <b>{`${validationResult.summary?.warning_count || ''} Warnungen`}</b> in der
              hochgeladenen Stammdatentabelle{' '}
              {!isExpanded && (
                <>
                  {' '}
                  <u>Details anzeigen</u>
                </>
              )}
            </>
          }
          description={
            isExpanded ? (
              <>
                <List
                  size="small"
                  bordered
                  dataSource={validationResult.warnings}
                  renderItem={(warn) => (
                    <List.Item>
                      Zeile {warn.row}, Spalte "{warn.column}": {warn.message}
                    </List.Item>
                  )}
                  style={{ margin: 0, paddingLeft: 0 }}
                />
                <p>
                  Hinweis: Zum Verarbeiten der Stammdatentabelle ist es nicht notwendig, dass die
                  Warnungen behoben werden. Sie sollten diese jedoch überprüfen und ggf. durch einen
                  erneuten Upload korrigieren.
                </p>
                {/* <p>
                  Sie können diese Warnungen möglicherweise in dem Bereich{' '}
                  <i>Bearbeitung der hochgeladenen Stammdatentabelle</i> korrigieren. Alternativ
                  können Sie die hochgeladene Stammdatentabelle (und die zugehörigen Abgleiche)
                  löschen und eine korrigierte Datei hochladen.
                </p> */}
              </>
            ) : null
          }
          style={{
            marginTop: validationResult.errors && validationResult.errors.length > 0 ? 12 : 0,
            cursor: isExpanded ? 'default' : 'pointer',
          }}
          onClick={() => setIsExpanded(true)}
        />
      )}

      {/* Show success if valid */}
      {validationResult && validationResult.valid === true && (
        <Alert
          type="success"
          showIcon
          // message="Die hochgeladene Stammdatentabelle ist valide, Sie können nun die Verarbeitung der Stammdaten auslösen."
          message="Die hochgeladene Stammdatentabelle ist valide und kann nun verarbeitet werden."
          style={{
            marginTop: 12,
          }}
        />
      )}
    </Card>
  );
};
